import type {
    IRegDataComparisonResult,
    IRegDataApplyResult,
    IRegDataPreviewRequest,
    IRegDataPreviewResult,
    ISpreadsheetTemplateActive,
    ISpreadsheetTemplateExtractListValuesResult,
    ISpreadsheetTemplateGetMappingResult,
    ISpreadsheetTemplateSaveMappingResult,
    ISpreadsheetTemplateUploadResult,
    ISpreadsheetTemplateValidateMappingResult,
    IWorkbookInspectionResponse,
    IWasteReturnTemplateMapping,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { authHeader, authHeaderNoContentType, handleResponse } from "../../utils";
import { downloadBlob, parseContentDispositionFileName } from "../../utils/downloadBlob";
import { parseApiError } from "../../utils/api/parseApiError";

const BASE = "/api/spreadsheet-templates/ea-waste-return";

export const spreadsheetTemplatesService = {
    getActive,
    upload,
    getWorkbookInspection,
    getMapping,
    saveMapping,
    validateMapping,
    validateMappingStructural,
    extractListValues,
    getRegDataComparison,
    previewRegDataChanges,
    applyRegDataChanges,
    testExport,
};

async function getActive(): Promise<ISpreadsheetTemplateActive> {
    const response = await fetch(`${BASE}/active`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    });
    return handleResponse(response);
}

async function upload(file: File, name?: string): Promise<ISpreadsheetTemplateUploadResult> {
    const formData = new FormData();
    formData.append("file", file);
    if (name?.trim()) {
        formData.append("name", name.trim());
    }

    const response = await fetch(`${BASE}/upload`, {
        method: "POST",
        headers: authHeaderNoContentType(),
        credentials: "include",
        body: formData,
    });
    return handleResponse(response);
}

async function getWorkbookInspection(refresh = false): Promise<IWorkbookInspectionResponse> {
    const query = refresh ? "?refresh=true" : "";
    const response = await fetch(`${BASE}/workbook-inspection${query}`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    });
    return handleResponse(response);
}

async function getMapping(): Promise<ISpreadsheetTemplateGetMappingResult> {
    const response = await fetch(`${BASE}/mapping`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    });
    return handleResponse(response);
}

async function saveMapping(mapping: IWasteReturnTemplateMapping): Promise<ISpreadsheetTemplateSaveMappingResult> {
    const response = await fetch(`${BASE}/mapping`, {
        method: "PUT",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(mapping),
    });
    return handleResponse(response);
}

async function validateMapping(mapping: IWasteReturnTemplateMapping): Promise<ISpreadsheetTemplateValidateMappingResult> {
    const response = await fetch(`${BASE}/validate-mapping`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(mapping),
    });
    return handleResponse(response);
}

async function validateMappingStructural(
    mapping: IWasteReturnTemplateMapping
): Promise<ISpreadsheetTemplateValidateMappingResult> {
    const response = await fetch(`${BASE}/validate-mapping?mode=structural`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(mapping),
    });
    return handleResponse(response);
}

async function extractListValues(): Promise<ISpreadsheetTemplateExtractListValuesResult> {
    const response = await fetch(`${BASE}/extract-list-values`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    });
    return handleResponse(response);
}

async function getRegDataComparison(): Promise<IRegDataComparisonResult> {
    const response = await fetch(`${BASE}/reg-data-comparison`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    });
    return handleResponse(response);
}

async function previewRegDataChanges(request: IRegDataPreviewRequest): Promise<IRegDataPreviewResult> {
    const response = await fetch(`${BASE}/reg-data-preview`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}

async function applyRegDataChanges(request: IRegDataPreviewRequest): Promise<IRegDataApplyResult> {
    const response = await fetch(`${BASE}/reg-data-apply`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(request),
    });

    if (response.status === 409) {
        const text = await response.text();
        let message = "Regulatory data changed since preview. Run Preview again before applying.";
        try {
            const data = text ? JSON.parse(text) : null;
            const apiError = parseApiError(data);
            if (apiError.message) message = apiError.message;
        } catch {
            if (text) message = text;
        }
        return Promise.reject(new Error(message));
    }

    return handleResponse(response);
}

async function testExport(): Promise<void> {
    const response = await fetch(`${BASE}/test-export`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    });

    if (!response.ok) {
        const text = await response.text();
        let message = "Could not generate the test export.";
        try {
            const data = text ? JSON.parse(text) : null;
            const apiError = parseApiError(data);
            if (apiError.message) message = apiError.message;
        } catch {
            if (text) message = text;
        }
        return Promise.reject(new Error(message));
    }

    const blob = await response.blob();
    const fileName =
        parseContentDispositionFileName(response.headers.get("content-disposition")) ??
        "ea-waste-return-test-export.xlsx";
    downloadBlob(blob, fileName);
}

export { downloadBlob, parseContentDispositionFileName };
